/**
 * useReferralCapture
 *
 * On mount, reads `?ref=CODE` from the URL, persists it in localStorage with a
 * configurable TTL (default 90 days), and fires a fire-and-forget click to the
 * `track-referral-click` edge function.
 *
 * The stored code survives navigation and is later attached to the user's
 * profile right after signup (see AuthContext) and to Stripe Checkout sessions
 * via the `create-checkout` edge function.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';

const STORAGE_KEY = 'careos_ref';
const DEFAULT_TTL_DAYS = 90;

interface StoredRef {
  code: string;
  capturedAt: number; // epoch ms
  ttlDays: number;
}

function readStored(): StoredRef | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredRef;
    const ageMs = Date.now() - parsed.capturedAt;
    const ttlMs = parsed.ttlDays * 24 * 60 * 60 * 1000;
    if (ageMs > ttlMs) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeStored(code: string, ttlDays = DEFAULT_TTL_DAYS) {
  try {
    const payload: StoredRef = {
      code,
      capturedAt: Date.now(),
      ttlDays,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* ignore */
  }
}

export function getReferralCode(): string | null {
  return readStored()?.code ?? null;
}

export function clearReferralCode() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function useReferralCapture() {
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const rawCode = params.get('ref') || params.get('utm_referral');
    if (!rawCode) return;

    const code = rawCode.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    if (!code || code.length < 3 || code.length > 32) return;

    const existing = readStored();
    // Last-touch attribution: always overwrite if a new code is presented.
    if (existing?.code !== code) {
      writeStored(code);
    }

    // Fire-and-forget click tracking. Don't await.
    const utmSource = params.get('utm_source');
    const utmMedium = params.get('utm_medium');
    const utmCampaign = params.get('utm_campaign');
    void supabase.functions
      .invoke('track-referral-click', {
        body: {
          code,
          landing_path: location.pathname + location.search,
          utm_source: utmSource,
          utm_medium: utmMedium,
          utm_campaign: utmCampaign,
        },
      })
      .catch(() => {
        /* swallow — analytics must never break UX */
      });
  }, [location.search, location.pathname]);
}
